import { StateContext } from "@ngxs/store";
import { patch, updateItem } from "@ngxs/store/operators";
import { AppDataStateModel } from "@shared/models/app-data.model";
import { CommonDataStateModel, CommonPageFilterSetting, CommonPageSetting, CommonSearchCriteria, CreateCommonPageFilterSetting } from "@shared/models/common-data.model";
import { UserProfile } from "@shared/classes/user-profile";
import { KpiConfigurationFieldName } from "@app/pages/kpi-configuration/kpi-configuration.constant";
import { WebApiResponse } from "@shared/classes/general";
import { BusinessHelper } from "@shared/utils/business-helper";
import { Utilities } from "../utils/utilities";

export class NgxsStoreHelpers {
  public static SetUserProfileByApexId(ctx: StateContext<CommonDataStateModel>, userProfileRes: WebApiResponse<UserProfile[]>, hackApexId: string) {
    if (userProfileRes && userProfileRes.data && userProfileRes.data.length > 0) {
      var userProfile = userProfileRes.data.find(item => item.apexId.toLocaleUpperCase() === hackApexId.toLocaleUpperCase());
      if (userProfile) {
        this.SetUserProfile(ctx, userProfile);
      }
    }
  }

  public static SetUserProfile(ctx: StateContext<CommonDataStateModel>, userProfile: UserProfile) {
    if (!userProfile || userProfile.apexId === undefined) {
      return;
    }

    var fullName = BusinessHelper.GetFullNameByFirstLastName(userProfile.fullName, userProfile.firstName, userProfile.lastName);
    userProfile.fullName = fullName;
    ctx.setState(patch({ profileMe: userProfile }));
  }

  public static SetPageViewInitialized(ctx: StateContext<AppDataStateModel>, functionName: string) {
    const commonPageSettings = ctx.getState().commonPageSettings, currentPage = commonPageSettings.find(p => p.functionName === functionName);
    if (!currentPage) {
      ctx.setState(patch({
        commonPageSettings: [
          ...commonPageSettings,
          {
            functionName: functionName,
            pageSearchInitialized: true
          } as CommonPageSetting
        ]
      }));
    }
    else {
      ctx.setState(patch({
        commonPageSettings: updateItem(item => item.functionName === functionName, patch({
          pageSearchInitialized: true
        }))
      }));
    }
  }

  public static SetKpiConfigurationSearchValueOfCountry(ctx: StateContext<AppDataStateModel>, country: string) {
    ctx.setState(patch({
      kpiConfiguration: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: updateItem(item => item.searchBy === KpiConfigurationFieldName.country, patch({
                searchValue: country
              }))
            })
          })
        })
      })
    }));
  }

  public static UpdatePageFilterSettingIntoStore(ctx: StateContext<AppDataStateModel>, setting: CommonPageFilterSetting, functionName: string) {
    if (setting) {
      NgxsStoreHelpers.SetPageFilterSetting(ctx, setting);
      return setting;
    }

    var existedSetting = ctx.getState().pageFilterSettings.find(item => item.functionName === functionName);
    if (existedSetting) {
      return existedSetting;
    }

    return CreateCommonPageFilterSetting(functionName);
  }

  private static SetPageFilterSetting(ctx: StateContext<AppDataStateModel>, setting: CommonPageFilterSetting) {
    const pageFilterSettings = ctx.getState().pageFilterSettings, currentPage = pageFilterSettings.find(p => p.functionName === setting.functionName);
    if (!currentPage) {
      ctx.setState(patch({
        pageFilterSettings: [
          ...pageFilterSettings,
          {
            functionName: setting.functionName,
            sortBy: setting.sortBy,
            sortDirection: setting.sortDirection,
            pageSize: setting.pageSize,
            pageIndex: setting.pageIndex
          } as CommonPageFilterSetting
        ]
      }));
    } else {
      ctx.setState(patch({
        pageFilterSettings: updateItem<CommonPageFilterSetting>(p => p.functionName === setting.functionName,
          patch({
            sortBy: setting.sortBy,
            sortDirection: setting.sortDirection,
            pageSize: setting.pageSize,
            pageIndex: setting.pageIndex
          }))
      }));
    }
  }

  public static SetDriverDataSearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      driverData: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }

  public static SetOrgUnitSearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      orgUnit: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }

  public static SetUserProfileSearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      userProfile: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }

  public static SetDrivingBehaviorSearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      drivingBehavior: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }

  public static SetItToolUsageSearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      itToolUsage: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }

  public static SetSafetySearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      safety: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }

  public static SetKpiEconomySearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      kpiEconomy: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }

  public static SetKpiOthersSearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      kpiOthers: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }

  public static SetCoachingSearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      coaching: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }

  public static SetAchievementSearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      achievement: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }

  public static SetAwardSearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      award: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }

  public static SetTeamMessageSearchCriteria(ctx: StateContext<AppDataStateModel>, searchCriteria: CommonSearchCriteria[]) {
    ctx.setState(patch({
      teamMessage: patch({
        search: patch({
          searchForm: patch({
            model: patch({
              searchCriteria: [...searchCriteria]
            })
          })
        })
      })
    }));
  }
}
